/** Мелкие общие части вкладок окна карточки площадки. */

import React from 'react'
import { cn } from '../../../../lib/cn'
import { NO_DATA } from './model'

export const Panel: React.FC<{
  title: string
  children: React.ReactNode
  aside?: React.ReactNode
}> = ({ title, children, aside }) => (
  <section className="rounded-lg border border-gray-200 p-4 dark:border-zinc-800">
    <div className="mb-3 flex items-center justify-between gap-3">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{title}</h3>
      {aside}
    </div>
    {children}
  </section>
)

/** Значение, которого нет: серое «нет данных» с причиной вместо прочерка. */
export const NoData: React.FC<{ why?: string }> = ({ why }) => (
  <span className="text-gray-400 dark:text-zinc-500" title={why ? `${NO_DATA}: ${why}` : NO_DATA}>
    {NO_DATA}
  </span>
)

export const ReadonlyValue: React.FC<{
  label: string
  value: React.ReactNode
  hint?: string
  mono?: boolean
  why?: string
}> = ({ label, value, hint, mono, why }) => {
  const empty = value === null || value === undefined || value === '' || value === '—'
  return (
    <div className="min-w-0">
      <div className="text-[11px] text-gray-500 dark:text-zinc-500">{label}</div>
      <div className={cn('mt-0.5 break-words text-sm text-gray-800 dark:text-zinc-200', mono && 'font-mono')}>
        {empty ? <NoData why={why} /> : value}
      </div>
      {hint && <div className="mt-0.5 text-[10px] text-gray-400 dark:text-zinc-500">{hint}</div>}
    </div>
  )
}

export const fieldClass = (invalid = false): string =>
  cn(
    'mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-zinc-950 dark:text-zinc-100',
    invalid ? 'border-red-400' : 'border-gray-300 dark:border-zinc-700',
  )

export interface SegmentedOption<T extends string> {
  key: T
  label: string
  disabled?: boolean
  title?: string
}

/** Переключатель режимов («Витрина / ПИМ / Разница»). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: Array<SegmentedOption<T>>
  value: T
  onChange: (key: T) => void
  ariaLabel: string
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5 dark:border-zinc-700 dark:bg-zinc-800"
    >
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          aria-pressed={value === option.key}
          disabled={option.disabled}
          title={option.title}
          onClick={() => onChange(option.key)}
          className={cn(
            'rounded px-2.5 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-40',
            value === option.key
              ? 'bg-white text-indigo-700 shadow-sm dark:bg-zinc-900 dark:text-indigo-300'
              : 'text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-zinc-100',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export const formatDateTime = (value?: string | null): string => {
  if (!value) return NO_DATA
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Плашка пояснения для вкладки: серая, с одним предложением. */
export const Notice: React.FC<{
  tone?: 'info' | 'warn' | 'bad'
  children: React.ReactNode
}> = ({ tone = 'info', children }) => (
  <div
    className={cn(
      'rounded-md px-3 py-2 text-xs',
      tone === 'bad' && 'bg-red-50 text-red-800 dark:bg-red-950/30 dark:text-red-300',
      tone === 'warn' && 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300',
      tone === 'info' && 'bg-gray-50 text-gray-600 dark:bg-zinc-950 dark:text-zinc-400',
    )}
  >
    {children}
  </div>
)
